import React from 'react';
import { Button, ButtonProps } from '@mui/material';
import { alpha, darken } from '@mui/material/styles';

// Loose typing so callers can pass `component={Link}` + `to=...` (or any
// other polymorphic root) without TypeScript fighting the generic
// ButtonProps signature.
type FilledActionButtonProps = ButtonProps & {
  component?: React.ElementType;
  to?: string;
  /**
   * When set, forwards to MUI `Button`'s built-in `loading` prop so an inline
   * spinner can be shown during async work (e.g. "Додати в словник" while
   * the POST is in flight).
   */
  loading?: boolean;
};

/**
 * Action button with a solid primary fill + white text. Used for top-of-page
 * actions on vocabulary + dictionary screens (Імпорт / Експорт / Створити
 * словник / Почати вправи / Додати слово / Додати в словник) where the
 * theme's default contained variant — rendered as a bordered transparent
 * button — would lose the visual weight these CTAs need.
 *
 * Disabled state stays in the same primary hue but at 45 % opacity so
 * the brief loading flash reads as "the same button, just waiting" instead
 * of falling back to a hard neutral gray block. Pages prefer to keep the
 * CTA always enabled and route empty-state clicks to a warning toast
 * rather than disable the control — that way the hero never has a dead
 * grey button in the middle of it.
 */
const FilledActionButton = React.forwardRef<
  HTMLButtonElement,
  FilledActionButtonProps
>(({ sx, loading, ...props }, ref) => {
  // Render through an untyped ElementType so callers can still pass a
  // polymorphic root (`component={Link}` + `to=...`) without TypeScript
  // fighting the generic ButtonProps signature. Core `Button` now handles the
  // `loading` prop natively (was previously delegated to lab's LoadingButton).
  const Root: React.ElementType = Button;
  return (
    <Root
      ref={ref}
      size={'small'}
      variant={'contained'}
      loading={loading}
      {...props}
      // Merge caller `sx` via the ARRAY form, not object spread. Object
      // spread ({ ...sx }) silently discards a *function* sx (a function has
      // no enumerable own props) — so `sx={(theme) => ({ ... })}` callers
      // used to have their styles vanish with no error. Array sx accepts
      // object | function | array elements and merges left-to-right, so both
      // forms work and later (caller) entries still win over the defaults.
      sx={[
        {
          bgcolor: 'primary.main',
          borderColor: 'primary.main',
          color: 'common.white',
          fontSize: 13.5,
          fontWeight: 600,
          height: 32,
          px: 1.75,
          '& .MuiButton-startIcon': { color: 'common.white' },
          '& svg': { color: 'common.white' },
          '&:hover': {
            bgcolor: (t: any) => darken(t.palette.primary.main, 0.1),
            borderColor: (t: any) => darken(t.palette.primary.main, 0.1),
            color: 'common.white',
          },
          '&.Mui-disabled': {
            bgcolor: (t: any) => alpha(t.palette.primary.main, 0.45),
            borderColor: 'transparent',
            color: 'rgba(255, 255, 255, 0.88)',
            cursor: 'not-allowed',
            '& svg': { color: 'rgba(255, 255, 255, 0.88)' },
          },
          // Inline spinner stays white on the primary fill — without this it
          // inherits the disabled grey when `loading` is paired with disabled.
          '& .MuiButton-loadingIndicator': { color: 'common.white' },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
});

FilledActionButton.displayName = 'FilledActionButton';

export default FilledActionButton;
